import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '~/libs/api.ts';
import { thunkErrorHandler } from '~/store/utils.ts';
import { APPLICATION_FILE_DOWNLOAD_DEFAULT_IS_ATTACHED } from '../constants.ts';
import {
  getApplicationFileParams,
  getDownloadedApplicationFileName,
} from '../utils.ts';
import type {
  TDeleteGuaranteesApplicationFileRequest,
  TDownloadGuaranteesApplicationFileRequest,
  TDownloadGuaranteesApplicationFileResponse,
  TFetchGuaranteesApplicationFileCapabilitiesRequest,
  TGuaranteesApplicationFileCapabilitiesResponse,
  TPatchGuaranteesApplicationFileCapabilitiesRequest,
  TPatchGuaranteesApplicationFileCapabilitiesResponse,
  TRegenerateGuaranteesApplicationFileRequest,
  TRegenerateGuaranteesApplicationFileResponse,
  TUploadGuaranteesApplicationFileRequest,
  TUploadGuaranteesApplicationFileResponse,
} from '../types.ts';

export const uploadGuaranteesApplicationFile = createAsyncThunk<
  TUploadGuaranteesApplicationFileResponse,
  TUploadGuaranteesApplicationFileRequest
>(
  'guaranteesPage/uploadApplicationFile',
  async ({ applicationId, fileId, fileType, multipartFile }, { rejectWithValue }) => {
    try {
      const formData = new FormData();

      formData.append('multipartFile', multipartFile);

      const { data } = await api.post<TUploadGuaranteesApplicationFileResponse>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files/${encodeURIComponent(fileType)}/upload`,
        formData,
        {
          params: getApplicationFileParams(fileId),
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const regenerateGuaranteesApplicationFile = createAsyncThunk<
  TRegenerateGuaranteesApplicationFileResponse,
  TRegenerateGuaranteesApplicationFileRequest
>(
  'guaranteesPage/regenerateApplicationFile',
  async ({ applicationId, fileId, fileType }, { rejectWithValue }) => {
    try {
      const { data } = await api.post<TRegenerateGuaranteesApplicationFileResponse>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files/${encodeURIComponent(fileType)}/regenerate`,
        null,
        {
          params: getApplicationFileParams(fileId),
        }
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const patchGuaranteesApplicationFileCapabilities = createAsyncThunk<
  TPatchGuaranteesApplicationFileCapabilitiesResponse,
  TPatchGuaranteesApplicationFileCapabilitiesRequest
>(
  'guaranteesPage/patchApplicationFileCapabilities',
  async ({ fileType, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch<TPatchGuaranteesApplicationFileCapabilitiesResponse>(
        `/guarantee-api/application-files/capabilities/${encodeURIComponent(fileType)}`,
        payload
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const downloadGuaranteesApplicationFile = createAsyncThunk<
  TDownloadGuaranteesApplicationFileResponse,
  TDownloadGuaranteesApplicationFileRequest
>(
  'guaranteesPage/downloadApplicationFile',
  async (
    {
      applicationId,
      fileId,
      fileType,
      isAttached = APPLICATION_FILE_DOWNLOAD_DEFAULT_IS_ATTACHED,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get<Blob>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files/${encodeURIComponent(fileType)}/${encodeURIComponent(fileId)}/download`,
        {
          headers: {
            Accept: '*/*',
          },
          params: {
            isAttached,
          },
          responseType: 'blob',
        }
      );
      const responseContentType = response.headers['content-type'];
      const contentType =
        typeof responseContentType === 'string' && responseContentType.trim()
          ? responseContentType
          : undefined;
      const contentDisposition = response.headers['content-disposition'];

      return {
        data: response.data,
        filename: getDownloadedApplicationFileName(contentDisposition, fileId),
        contentType,
      };
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApplicationFileCapabilities = createAsyncThunk<
  TGuaranteesApplicationFileCapabilitiesResponse,
  TFetchGuaranteesApplicationFileCapabilitiesRequest
>(
  'guaranteesPage/fetchApplicationFileCapabilities',
  async ({ applicationId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesApplicationFileCapabilitiesResponse>(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files/capabilities`
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const fetchGuaranteesApplicationFileTypeCapabilities = createAsyncThunk<
  TGuaranteesApplicationFileCapabilitiesResponse,
  void
>(
  'guaranteesPage/fetchApplicationFileTypeCapabilities',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<TGuaranteesApplicationFileCapabilitiesResponse>(
        '/guarantee-api/application-files/capabilities'
      );

      return data;
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);

export const deleteGuaranteesApplicationFile = createAsyncThunk<
  void,
  TDeleteGuaranteesApplicationFileRequest
>(
  'guaranteesPage/deleteApplicationFile',
  async ({ applicationId, fileId, fileType }, { rejectWithValue }) => {
    try {
      await api.delete(
        `/guarantee-api/applications/${encodeURIComponent(applicationId)}/application-files/${encodeURIComponent(fileType)}/${encodeURIComponent(fileId)}`
      );
    } catch (error) {
      return thunkErrorHandler(error, rejectWithValue);
    }
  }
);
